import {
  type CSSProperties,
  type FocusEvent,
  type ReactElement,
  type ReactNode,
  useId,
  useRef,
} from "react";

import { styled } from "@mui/material";

type Props = {
  title: ReactNode;
  children: ReactElement;
  disabled?: boolean;
};

const TT = "&:popover-open";
const EASE = "cubic-bezier(0.23, 1, 0.32, 1)";

// The bubble is promoted to the top layer (popover) so it escapes the overflow
// clipping AND the ancestor stacking contexts (e.g. a scrollable table) that a
// plain positioned tooltip can't. Placement stays pure CSS via anchor
// positioning; only the hover open/close needs JS (CSS can't open a popover).
const Tooltip = ({ children, disabled = false, title }: Props): ReactNode => {
  const id = useId();
  const anchorName = `--tt-${id.replace(/:/g, "")}`;
  const bubbleRef = useRef<HTMLSpanElement | null>(null);

  if (!title || disabled) {
    return children;
  }

  const setBubble = (el: HTMLSpanElement | null): void => {
    bubbleRef.current = el;
    if (el?.togglePopover && !el.hasAttribute("popover")) {
      el.setAttribute("popover", "manual");
    }
  };

  const show = (): void => {
    bubbleRef.current?.togglePopover?.(true);
  };

  const hide = (): void => {
    bubbleRef.current?.togglePopover?.(false);
  };

  // A dialog opened from the trigger restores focus to it on close, which would
  // reopen the tooltip with the pointer nowhere near it — so keyboard focus only.
  const showOnFocus = (event: FocusEvent<HTMLElement>): void => {
    if (event.target.matches(":focus-visible")) {
      show();
    }
  };

  return (
    <Trigger
      data-tooltip-anchor
      onBlur={hide}
      onClick={hide}
      onFocus={showOnFocus}
      onPointerEnter={show}
      onPointerLeave={hide}
      style={{ "--anchor-name": anchorName } as CSSProperties}
    >
      {children}
      <Bubble aria-hidden ref={setBubble} role="tooltip">
        {title}
      </Bubble>
    </Trigger>
  );
};

const Trigger = styled("span")({
  display: "inline-flex",
  justifyContent: "center",
  anchorName: "var(--anchor-name)",
});

const Bubble = styled("span")(({ theme }) => ({
  position: "fixed",
  inset: "auto",
  margin: 0,
  border: "none",
  fontWeight: "normal",
  positionAnchor: "var(--anchor-name)",
  positionArea: "block-end",
  positionTryFallbacks: "flip-block",
  marginTop: theme.spacing(1),
  zIndex: theme.zIndex.tooltip,
  backgroundColor: theme.palette.cf.cfBlack,
  fontSize: 13,
  padding: "4px 8px",
  borderRadius: 4,
  maxWidth: 300,
  width: "max-content",
  whiteSpace: "normal",
  pointerEvents: "none",
  opacity: 0,
  display: "none",
  transform: "translateY(-4px)",
  transition: `opacity 150ms ${EASE}, transform 150ms ${EASE}, display 150ms allow-discrete`,
  "&&&": {
    color: theme.palette.cf.cfWhite,
  },

  [TT]: {
    display: "block",
    opacity: 1,
    transform: "translateY(0)",
    "@starting-style": {
      opacity: 0,
      transform: "translateY(-4px)",
    },
  },
}));

export { Tooltip };
